/**
 * Where a world's name goes — a small greedy layout.
 *
 * Forty names on a 344-unit field cannot all sit "just outside" their
 * world: they collide with each other, with neighbouring bodies, with
 * the gate labels, with the family names on the rim, and with the
 * frame.  So each name tries a short list of spots — radially out, above,
 * below, radially in, further out, sideways, a step off the radius — and
 * takes the cheapest.  The cost is what a reader would pay: a spot off
 * the frame is unacceptable; a spot over a body or another name costs
 * the area it steals; and a name whose nearest body is not its own
 * costs the most, because attribution is the one thing a label cannot
 * get wrong.
 *
 * Outer rings place first (they have the fewest options), then three
 * more passes let an early bad choice be taken back once its neighbours
 * have shown their hands.
 *
 * Two rules keep forty names calm rather than merely legal.  A name is
 * pulled HARD toward its own body, so the sky reads as forty labelled
 * worlds instead of a field of type that happens not to collide.  And a
 * leader line is drawn only where one is EARNED — where another body
 * sits about as close, or where the name had to stand well off — never
 * merely because a name sits to one side, which is where the reference
 * drew half of them and why the sky looked stitched.
 *
 * Pure: bodies and obstacles in, placements out.  The orrery draws what
 * comes back; the tests hold it to no overlaps and no clipping.
 */

import { CENTRE, type Camera, frameUnder } from "./layout";

export type Anchor = "start" | "middle" | "end";
export type Box = [number, number, number, number];

export type LabelSubject = {
  id: string;
  x: number;
  y: number;
  /** The body's drawn radius, in field units. */
  r: number;
  /** The name as one row where it fits, or already broken where it does not. */
  rows: string[];
  /** The same name broken over two rows, when it has a space to break at.
      The engine reaches for this only when the one-row form has nowhere
      to stand — a second row doubles a label's weight, so it is earned
      rather than assumed. */
  rowsAlt?: string[];
  sun: boolean;
  /** On the outermost ring — under the altitudes seating the rim belongs to
      the family names, so these prefer to name themselves inward. */
  outer: boolean;
  /** The orbit out, 0 innermost; unplaced worlds count as beyond the last.
      Outer rings place first — they have the fewest options. */
  slot: number;
};

export type Placement = {
  /** The text anchor, relative to the body's centre, in field units. */
  lx: number;
  ly: number;
  anchor: Anchor;
  fontSize: number;
  rows: string[];
  /** Draw a leader from the body to the name. */
  lead: boolean;
};

export type LabelMode = "sky" | "ring" | "sector";

export type LabelInput = {
  subjects: LabelSubject[];
  /** Every body on view, subjects included — the dearest obstacles, and the ownership check. */
  bodies: { id: string; x: number; y: number; r: number }[];
  /** Things a name should not straddle, cheap: the column dividers. */
  obstacles: Box[];
  /** Things a name may not cover, dear: other type. */
  texts: Box[];
  mode: LabelMode;
  cam: Camera;
  /** Multiplies every font size — a phone renders the fixed viewBox at
      fewer CSS pixels, so the type has to grow to stay readable. */
  textScale: number;
};

/** Mono type: a glyph is about 0.62 em wide.  Measured, not drawn. */
export const GLYPH = 0.62;

/**
 * The name's rows.  A two-word name too long for one line at sky zoom
 * breaks at its space, the way the reference hand-broke "Inductive Bias"
 * — derived here, so a new world gets the same treatment unasked.
 */
export function rowsOf(name: string, mode: LabelMode): string[] {
  // A second row doubles a label's visual weight, so a name earns one
  // only when it is genuinely long.  The threshold rose with the smaller
  // sky type: at 5.3 units, thirteen characters still sit inside the gap
  // between two neighbouring worlds.  Everything shorter, and every name
  // at the zooms that have room, starts as one row — and falls back to
  // `rowsAltOf` only where the layout cannot seat it (see `LabelSubject`).
  if (mode !== "sky") return [name.trim() || ""];
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 2 && name.length > 13) return [words[0], words[1]];
  if (words.length === 3 && name.length > 16) return [words[0], `${words[1]} ${words[2]}`];
  return [name.trim() || ""];
}

/**
 * The same name over two rows, split as evenly as its spaces allow, or
 * `undefined` for a name with nowhere to break.  Half the width for
 * twice the height, which is the trade a crowded neighbourhood wants.
 */
export function rowsAltOf(name: string): string[] | undefined {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return undefined;
  let best: [string, string] | null = null;
  let evenest = Infinity;
  for (let cut = 1; cut < words.length; cut += 1) {
    const head = words.slice(0, cut).join(" ");
    const tail = words.slice(cut).join(" ");
    const gap = Math.abs(head.length - tail.length);
    if (gap < evenest) {
      evenest = gap;
      best = [head, tail];
    }
  }
  return best ?? undefined;
}

const widest = (rows: string[]) => rows.reduce((m, row) => Math.max(m, row.length), 0);

const intersects = (a: Box, b: Box) => !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
const overlapArea = (a: Box, b: Box) =>
  Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) *
  Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
const pad = (b: Box): Box => [b[0] - 1.4, b[1] - 1.1, b[2] + 1.4, b[3] + 1.1];
const gapTo = (b: Box, x: number, y: number) =>
  Math.hypot(Math.max(b[0] - x, 0, x - b[2]), Math.max(b[1] - y, 0, y - b[3]));

/** Visits the index of every box that could touch the query box. */
type Neighbourhood = (query: Box, visit: (index: number) => void) => void;

/** Field units to a grid cell — a little wider than the longest name. */
const CELL = 24;

/**
 * A coarse grid over a list of boxes, so asking what a name touches
 * costs a handful of tests rather than a scan of the field.
 *
 * The engine asks that question tens of thousands of times per layout,
 * and at sky zoom it asks it of some three hundred column-divider marks
 * and seventy boxes of type — nearly all of them on the far side of the
 * map.  A box straddling two cells is listed under both, so each query
 * stamps the indices it has already answered for.
 */
function neighbourhood(boxes: Box[]): Neighbourhood {
  // Cells are offset before keying so a negative column cannot alias a row.
  const key = (cx: number, cy: number) => (cy + 512) * 4096 + (cx + 512);
  const cells = new Map<number, number[]>();
  boxes.forEach((box, index) => {
    for (let cy = Math.floor(box[1] / CELL); cy <= Math.floor(box[3] / CELL); cy += 1) {
      for (let cx = Math.floor(box[0] / CELL); cx <= Math.floor(box[2] / CELL); cx += 1) {
        const at = key(cx, cy);
        const list = cells.get(at);
        if (list) list.push(index);
        else cells.set(at, [index]);
      }
    }
  });
  const seen = new Int32Array(boxes.length).fill(-1);
  let stamp = 0;
  return (query, visit) => {
    stamp += 1;
    for (let cy = Math.floor(query[1] / CELL); cy <= Math.floor(query[3] / CELL); cy += 1) {
      for (let cx = Math.floor(query[0] / CELL); cx <= Math.floor(query[2] / CELL); cx += 1) {
        const list = cells.get(key(cx, cy));
        if (!list) continue;
        for (let k = 0; k < list.length; k += 1) {
          const index = list[k];
          if (seen[index] === stamp) continue;
          seen[index] = stamp;
          visit(index);
        }
      }
    }
  };
}

type Spot = { lx: number; ly: number; anchor: Anchor };

export function layoutLabels(input: LabelInput): Map<string, Placement> {
  const { subjects, bodies, obstacles, texts, mode, cam, textScale } = input;
  const near = mode !== "sky";
  const frame = frameUnder(cam);
  // Near the camera scales the drawing up, so a name is set in fewer field
  // units to keep its screen size steady; the reference's own ratio.
  const zoom = near ? Math.max(1, cam.s) * 1.3 : 1;
  const stretch = near ? 1.3 : 1;
  // At sky zoom every name is set at one size — a legend, not a
  // hierarchy — and it is small: forty of them share the field, and the
  // ring and column zooms are where a world is read rather than found.
  // Near the camera a name is set from the room its own length needs,
  // capped so a short name on a crowded ring does not balloon to fill
  // the gap it happens to have.
  const fontOf = (rows: string[]) =>
    (near ? Math.min(7, 118 / Math.max(1, widest(rows))) / zoom : 5.3) * stretch * textScale;

  const box = (subject: LabelSubject, spot: Spot, rows: string[]): Box => {
    const fs = fontOf(rows);
    const height = fs * 1.1 + (rows.length - 1) * fs * 1.15;
    const w = widest(rows) * fs * GLYPH;
    const X = subject.x + spot.lx * stretch;
    const Y = subject.y + spot.ly * stretch;
    const x0 = spot.anchor === "start" ? X : spot.anchor === "end" ? X - w : X - w / 2;
    return [x0, Y - height + 1.4, x0 + w, Y + 1.6];
  };

  /** Nudge a spot back inside the frame rather than let it clip. */
  const clampToFrame = (subject: LabelSubject, spot: Spot, rows: string[]): Spot => {
    const b = box(subject, spot, rows);
    let dx = 0;
    let dy = 0;
    if (b[0] < frame.x0 + 1) dx = frame.x0 + 1 - b[0];
    else if (b[2] > frame.x1 - 1) dx = frame.x1 - 1 - b[2];
    if (b[1] < frame.y0 + 1) dy = frame.y0 + 1 - b[1];
    else if (b[3] > frame.y1 - 1) dy = frame.y1 - 1 - b[3];
    return dx || dy ? { lx: spot.lx + dx / stretch, ly: spot.ly + dy / stretch, anchor: spot.anchor } : spot;
  };

  const offFrame = (b: Box) =>
    Math.max(0, frame.x0 - b[0]) + Math.max(0, b[2] - frame.x1) +
    Math.max(0, frame.y0 - b[1]) + Math.max(0, b[3] - frame.y1);
  const nearObstacles = neighbourhood(obstacles);
  const nearTexts = neighbourhood(texts);
  const nearBodies = neighbourhood(
    bodies.map((body): Box => [body.x - body.r, body.y - body.r, body.x + body.r, body.y + body.r]),
  );

  // An overlap is not a trade-off against a longer leader: it costs
  // enough that only a field with no clear spot at all pays it.
  const hits = (within: Neighbourhood, list: Box[], b: Box, each: number, perArea: number) => {
    let sum = 0;
    within(b, (index) => {
      const other = list[index];
      if (intersects(other, b)) sum += overlapArea(other, b) * perArea + each;
    });
    return sum;
  };

  /**
   * How far a name's box reaches into any world but its own — measured
   * against the DISC, not a box around it.
   *
   * The difference is the corners, and the corners are exactly where a
   * tidy name wants to sit: the spot diagonally off a world is close,
   * clear, and reads as belonging to it.  Squaring the obstacle fenced
   * those off and pushed names out to the far spots instead.
   *
   * Every world counts, the name's own included: a label over the planet
   * it names is the one overlap a reader cannot resolve at all.
   */
  const intoBody = (b: Box) => {
    let k = 0;
    nearBodies(b, (index) => {
      const body = bodies[index];
      const nx = Math.max(b[0], Math.min(body.x, b[2]));
      const ny = Math.max(b[1], Math.min(body.y, b[3]));
      const dx = body.x - nx;
      const dy = body.y - ny;
      const depth = body.r - Math.sqrt(dx * dx + dy * dy);
      // Dearer than any other fault, and deliberately so.  Where a field
      // is dense enough that something must give — the innermost ring on
      // a phone — a name touching another NAME is a reader's minor
      // annoyance, while a name over a WORLD hides the thing the map is
      // for.  Priced this far apart, the layout always spends the first.
      if (depth > 0) k += 4200 + depth * 420;
    });
    return k;
  };

  const placed = new Map<string, Box>();

  /**
   * What a reader would pay for this spot — or, once the spot is out of
   * the running, any number at or above `ceiling`.
   *
   * Every term is non-negative, so the running total only climbs: the
   * moment it reaches the cheapest cost seen so far, the spot cannot win
   * and the rest of the work is waste.  The terms are therefore ordered
   * decisive-and-cheap first.  The pull toward the world being named
   * needs no scan at all, and on the grid sweep — where nearly every
   * spot is further from the body than the one already chosen — it alone
   * rejects the field before a single overlap is measured.
   */
  const cost = (b: Box, subject: LabelSubject, ceiling: number) => {
    // Whose name is this?  Measured from the name's centre, which is the
    // reading a reader makes.
    const cx = (b[0] + b[2]) / 2;
    const cy = (b[1] + b[3]) / 2;
    const ox = cx - subject.x;
    const oy = cy - subject.y;
    const own = Math.sqrt(ox * ox + oy * oy);
    // The distance term is what makes the sky look tidy rather than
    // merely non-overlapping: at a low weight the optimiser is happy to
    // fling a name across the field to save a few units of overlap, and
    // forty such decisions read as scatter.  Priced this high, a name
    // leaves its world's side only when it truly has nowhere to stand.
    let k = own * own * 1.6;
    if (k >= ceiling) return k;
    k += offFrame(b) * 260;
    if (k >= ceiling) return k;

    const bp = pad(b);
    // covering a world is the dearest fault; straddling a divider the cheapest
    k += intoBody(bp);
    if (k >= ceiling) return k;
    k += hits(nearObstacles, obstacles, bp, 60, 10) + hits(nearTexts, texts, bp, 1600, 160);
    if (k >= ceiling) return k;

    for (const [otherId, other] of placed) {
      if (otherId !== subject.id && intersects(other, bp)) k += overlapArea(other, bp) * 60 + 1400;
    }
    if (k >= ceiling) return k;

    // The nearest body that is not its own: squared while it is only
    // being compared, rooted once at the end.
    let nearestSq = Infinity;
    for (let i = 0; i < bodies.length; i += 1) {
      const body = bodies[i];
      if (body.id === subject.id) continue;
      const dx = cx - body.x;
      const dy = cy - body.y;
      const d = dx * dx + dy * dy;
      if (d < nearestSq) nearestSq = d;
    }
    // Close to a body, a name reads by adjacency and ambiguity is dear;
    // further out it will get a leader, which settles whose it is.
    const ambiguity = Math.max(0, own - Math.sqrt(nearestSq) + 6);
    return k + ambiguity * (own < 14 ? 500 : 60);
  };

  const candidates = (subject: LabelSubject, index: number): Spot[] => {
    const dx = subject.x - CENTRE;
    const dy = subject.y - CENTRE;
    const d0 = Math.hypot(dx, dy) || 1;
    const ux = dx / d0;
    const uy = dy / d0;
    const angle = Math.atan2(dy, dx);
    const radialAnchor = (side: number): Anchor =>
      Math.abs(ux) < 0.35 ? "middle" : (ux > 0) === side > 0 ? "start" : "end";
    const radial = (side: number, reach: number): Spot => ({
      lx: ux * side * reach, ly: uy * side * reach + 1.6, anchor: radialAnchor(side),
    });
    const vertical = (side: number): Spot => ({
      lx: 0, ly: side > 0 ? 12.4 : near ? -14.5 : -9.6, anchor: "middle",
    });
    const sideways = (side: number): Spot => ({
      lx: side * (near ? 13 : 11), ly: 2.2, anchor: side > 0 ? "start" : "end",
    });
    const step = (side: number, up: number): Spot => ({
      lx: -Math.sin(angle) * side * 11,
      ly: Math.cos(angle) * side * 11 + (up > 0 ? 7 : -5),
      anchor: "middle",
    });
    /** The eight compass points at a reach — the fallbacks a crowded field needs. */
    const compass = (reach: number): Spot[] =>
      [45, 135, 225, 315, 0, 90, 180, 270].map((deg) => {
        const a = (deg * Math.PI) / 180;
        const cx = Math.cos(a);
        const cy = Math.sin(a);
        return {
          lx: cx * reach,
          ly: cy * reach + (Math.abs(cy) < 0.3 ? 2 : cy > 0 ? 4 : 0),
          anchor: Math.abs(cx) < 0.35 ? "middle" : cx > 0 ? "start" : "end",
        };
      });
    if (subject.sun) return [{ lx: 0, ly: 12.8, anchor: "middle" }, { lx: 0, ly: -11, anchor: "middle" }];
    /** A step off the radius at a farther reach, for a crowded orbit. */
    const stepFar = (side: number, up: number): Spot => ({
      lx: -Math.sin(angle) * side * 18,
      ly: Math.cos(angle) * side * 18 + (up > 0 ? 7 : -5),
      anchor: "middle",
    });
    if (near) {
      const alt = index % 2 ? -1 : 1;
      const reach = 13.5;
      return [
        radial(1, reach), vertical(alt), vertical(-alt), radial(-1, reach),
        radial(1, 25), radial(-1, 25), sideways(1), sideways(-1),
        step(1, 1), step(-1, 1), step(1, -1), step(-1, -1),
        stepFar(1, 1), stepFar(-1, 1), stepFar(1, -1), stepFar(-1, -1),
        ...compass(15), ...compass(24), ...compass(32),
      ];
    }
    const reach = 11.4;
    const tall = (side: number): Spot => ({ lx: 0, ly: side > 0 ? 19 : -16, anchor: "middle" });
    // Ordered by how tidy they look, not by how much room they have: the
    // optimiser prices distance heavily, so the near spots win unless
    // they genuinely collide.
    const extra = [
      sideways(1), sideways(-1), radial(1, 16), radial(-1, 16), tall(1), tall(-1),
      step(1, 1), step(-1, 1), step(1, -1), step(-1, -1),
      radial(1, 22), radial(-1, 22), stepFar(1, 1), stepFar(-1, 1), stepFar(1, -1), stepFar(-1, -1),
      ...compass(13), ...compass(19), ...compass(26),
    ];
    // The rim belongs to the family names, so the outermost ring names
    // itself inward first; everyone else reaches out.
    const first = subject.outer
      ? [radial(-1, reach), vertical(1), vertical(-1), radial(1, reach)]
      : [radial(1, reach), vertical(1), vertical(-1), radial(-1, reach)];
    return first.concat(extra);
  };

  // The sun first, then the outer rings — fewest options — then inward;
  // near the camera the subjects arrive already in ring order.
  const order = near
    ? subjects
    : subjects.slice().sort((a, b) => Number(b.sun) - Number(a.sun) || b.slot - a.slot);
  // The geometry of a spot does not depend on how the name is broken, so
  // the candidate list is built once and each row form clamps it itself.
  const options = new Map<string, Spot[]>();
  order.forEach((subject, index) => {
    options.set(subject.id, candidates(subject, index));
  });

  // A cost this high means the spot covers something.  The named spots are
  // the graceful ones; when every one of them collides, the field around
  // the body is searched on a grid for the cheapest clear spot instead —
  // a name further off with a leader beats a name on top of a neighbour.
  const COLLIDES = 1000;
  /** What a one-row name is worth keeping, priced against a collision. */
  const WRAP_RELUCTANCE = 260;
  const swept = (subject: LabelSubject): Spot[] => {
    const spots: Spot[] = [];
    const clear = subject.r + 2;
    // Reaches as far as the body is big, and no bound tighter than that:
    // the quadratic distance term is what keeps a swept name near, so
    // capping the search only costs the crowded few their last option.
    const span = Math.max(32, subject.r * 3.6);
    for (let dy = -span; dy <= span; dy += 2.5) {
      for (let dx = -span - 2; dx <= span + 2; dx += 2.5) {
        if (dx * dx + dy * dy < clear * clear) continue;
        // the anchor decides which way the box extends from the spot,
        // which is what lets a name tuck into a gap on one side of it
        (["middle", "start", "end"] as Anchor[]).forEach((anchor) => {
          spots.push({ lx: dx / stretch, ly: dy / stretch + 2, anchor });
        });
      }
    }
    return spots;
  };

  type Seat = { spot: Spot; rows: string[] };
  const cheapest = (subject: LabelSubject, spots: Spot[], rows: string[], from: number) => {
    let pick: Seat | null = null;
    let best = from;
    for (let i = 0; i < spots.length; i += 1) {
      const spot = clampToFrame(subject, spots[i], rows);
      // The best cost so far is also the bound: a spot that reaches it
      // has already lost, and `cost` stops there.
      const k = cost(box(subject, spot, rows), subject, best);
      if (k < best) {
        best = k;
        pick = { spot, rows };
      }
    }
    return { pick, best };
  };

  const chosen = new Map<string, Seat>();
  const seated = (a: Seat | undefined, b: Seat) =>
    !!a && a.spot.lx === b.spot.lx && a.spot.ly === b.spot.ly &&
    a.spot.anchor === b.spot.anchor && a.rows.length === b.rows.length;
  // Four passes, not one: the first is greedy in ring order and the rest
  // let an early choice be taken back once its neighbours have shown
  // their hands.  Past five the seating stops moving — and a pass that
  // moves nobody leaves the field exactly as it found it, so every pass
  // after it would decide the same way.  That is the fixed point, and
  // most skies reach it in three.
  for (let pass = 0; pass < 5; pass += 1) {
    let moved = false;
    order.forEach((subject) => {
      const spots = options.get(subject.id);
      if (!spots) return;
      placed.delete(subject.id);
      // The name as written, in its best spot.  Then, only if that spot
      // collides with something, the same name over two rows — and only
      // then the sweep, which trades the graceful spots for any clear one.
      let { pick, best } = cheapest(subject, spots, subject.rows, Infinity);
      const alt = subject.rowsAlt;
      if (best >= COLLIDES && alt) {
        // A second row is half the width for twice the height, which a
        // crowded orbit wants and an empty one does not — so it is worth
        // a little to stay on one row, and the layout pays that only
        // when one row genuinely fits.
        const wrapped = cheapest(subject, spots, alt, best - WRAP_RELUCTANCE);
        if (wrapped.pick) ({ pick, best } = wrapped);
      }
      if (best >= COLLIDES && !subject.sun) {
        const sweep = swept(subject);
        for (const rows of alt ? [subject.rows, alt] : [subject.rows]) {
          const found = cheapest(subject, sweep, rows, best);
          if (found.pick) ({ pick, best } = found);
        }
      }
      const seat = pick ?? { spot: spots[0], rows: subject.rows };
      if (!seated(chosen.get(subject.id), seat)) moved = true;
      chosen.set(subject.id, seat);
      placed.set(subject.id, box(subject, seat.spot, seat.rows));
    });
    if (!moved) break;
  }

  const out = new Map<string, Placement>();
  order.forEach((subject) => {
    const seat = chosen.get(subject.id);
    if (!seat) return;
    const { spot, rows } = seat;
    let { lx, ly } = spot;
    let lead = false;
    if (!subject.sun) {
      const b = placed.get(subject.id) as Box;
      const own = gapTo(b, subject.x, subject.y) - subject.r;
      // Whose is it, measured from the name's centre — the same reading a
      // reader makes, and the one the tests hold the engine to.
      const cx = (b[0] + b[2]) / 2;
      const cy = (b[1] + b[3]) / 2;
      const ownCentre = Math.hypot(cx - subject.x, cy - subject.y);
      let otherCentre = Infinity;
      bodies.forEach((body) => {
        if (body.id !== subject.id) otherCentre = Math.min(otherCentre, Math.hypot(cx - body.x, cy - body.y));
      });
      // A leader answers one question — WHOSE name is this? — so it is
      // drawn only where the question can be asked: another body sits
      // about as close, or the name had to stand well off its own.  A
      // name snug against its world has already answered it, and a
      // tether there is a stitch in a sky that wants to be calm.
      lead = otherCentre < ownCentre + 7 || own > 9;
      if (lead && own < 5) {
        const d = Math.hypot(lx, ly) || 1;
        const k = 5 - own;
        const nudged = clampToFrame(subject, { lx: lx + (lx / d) * k, ly: ly + (ly / d) * k, anchor: spot.anchor }, rows);
        lx = nudged.lx;
        ly = nudged.ly;
      }
    }
    out.set(subject.id, {
      lx: lx * stretch, ly: ly * stretch, anchor: spot.anchor, fontSize: fontOf(rows), rows, lead,
    });
  });
  return out;
}

/** The box a placement occupies, for the tests and the drawing alike. */
export function placementBox(subject: { x: number; y: number }, placement: Placement): Box {
  const fs = placement.fontSize;
  const n = placement.rows.length;
  const height = fs * 1.1 + (n - 1) * fs * 1.15;
  const w = widest(placement.rows) * fs * GLYPH;
  const X = subject.x + placement.lx;
  const Y = subject.y + placement.ly;
  const x0 = placement.anchor === "start" ? X : placement.anchor === "end" ? X - w : X - w / 2;
  return [x0, Y - height + 1.4, x0 + w, Y + 1.6];
}
